import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface RecipeComponent {
  stockItemId: string;
  quantity: number;
  unit: string;
  yieldPercentage?: number;
}

export interface Recipe {
  id: string;
  cnpj: string;
  name: string;
  description?: string;
  components: RecipeComponent[];
  createdAt: string;
  updatedAt: string;
}

export interface ListRecipesParams {
  cnpj?: string;
  searchTerm?: string;
}

export interface CreateRecipeRequest {
  name: string;
  description?: string;
  components: RecipeComponent[];
}

export interface UpdateRecipeRequest {
  name?: string;
  description?: string;
  components?: RecipeComponent[];
}

class RecipeService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  async listRecipes(params?: ListRecipesParams): Promise<Recipe[]> {
    try {
      const response: AxiosResponse<Recipe[]> = await this.client.get('/admin/recipes', {
        params: params?.cnpj || params?.searchTerm ? params : undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar fichas técnicas');
    }
  }

  async createRecipe(data: CreateRecipeRequest): Promise<Recipe> {
    try {
      const response: AxiosResponse<Recipe> = await this.client.post('/admin/recipes', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar ficha técnica');
    }
  }

  async updateRecipe(id: string, data: UpdateRecipeRequest): Promise<Recipe> {
    try {
      const response: AxiosResponse<Recipe> = await this.client.put(`/admin/recipes/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar ficha técnica');
    }
  }

  async deleteRecipe(id: string, cnpj?: string): Promise<void> {
    try {
      await this.client.delete(`/admin/recipes/${id}`, {
        params: cnpj ? { cnpj } : undefined,
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao excluir ficha técnica');
    }
  }
}

export const recipeService = new RecipeService();
export default recipeService;
